import RefData from 'ref-data';
import _ from 'lodash';

import { takeLatest, put, call, all, race, select, take, takeEvery } from 'redux-saga/effects';
import delay from '@redux-saga/delay-p';

import Experience from '../experience';
import User from '../user';

import ExportTaskApi from './export-task-api';
import ExportTaskActions from './export-task-actions';
import ExportTaskSelectors from './export-task-selectors';

function* bootstrapExportTasksHandler({ payload: { experienceId, exportType } }) {
  try {
    const isInitialLoad = yield select(ExportTaskSelectors.isInitialLoad, exportType);
    let _currentActiveTask = yield select(ExportTaskSelectors.activeTask, exportType);

    const canExportContent = yield select(User.Selectors.canExportContent);

    // grab the export task if we don't have one already
    if (_.isEmpty(_currentActiveTask)) {
      // fetch the last export
      yield put(ExportTaskActions.fetchLastExport(experienceId, null, exportType));

      const { success, failure } = yield race({
        success: take(
          (action) =>
            action.type === ExportTaskActions.fetchLastExportSuccess.getType() &&
            action.payload.exportType === exportType
        ),
        failure: take(
          (action) =>
            action.type === ExportTaskActions.fetchLastExportFailure.getType() &&
            action.payload.exportType === exportType
        ),
      });

      if (success) {
        _currentActiveTask = success.payload.exportTask;

        // set up the watcher only on the initial load
        if (canExportContent && isInitialLoad) yield put(ExportTaskActions.watchExportTaskPolling(exportType));

        // if we have an export task in place and it says it's still in progress, we should start polling to make sure the UI gets updated appropriately in order to wait for the export to finish
        if (_currentActiveTask.isInProgress)
          yield put(ExportTaskActions.startPollingExportTask(experienceId, _currentActiveTask.exportId, exportType));
      } else if (failure) {
        yield put(ExportTaskActions.bootstrapExportTasksFailure(failure.payload.error));
      }
    }

    yield put(ExportTaskActions.bootstrapExportTasksSuccess(_currentActiveTask, exportType));
  } catch (error) {
    yield put(ExportTaskActions.bootstrapExportTasksFailure(error));
  }
}

/**
 *
 * @param {object} payload the experience id (experienceId), export id (exportId) the api call needs
 */
function* fetchLastExportHandler({ payload: { experienceId, exportId, exportType } }) {
  try {
    const { interactionStrategyIsPartition } = yield select(Experience.Selectors.experience),
      _lastExport = yield call(ExportTaskApi.fetchLastExport, {
        id: experienceId,
        exportId,
        interactionStrategyIsPartition,
        exportType,
      });
    yield put(ExportTaskActions.fetchLastExportSuccess(_lastExport, exportType));
    //yield put(ExportTaskActions.completePollingExportTask());
  } catch (error) {
    yield put(ExportTaskActions.fetchLastExportFailure(error, exportType));
    //yield put(ExportTaskActions.completePollingExportTask());
  }
}

function* watchExportTaskPollingHandler({ payload: { exportType } }) {
  while (true) {
    const {
      payload: { experienceId, exportId },
    } = yield take(
      (action) =>
        action.type === ExportTaskActions.startPollingExportTask.getType() && action.payload.exportType === exportType
    );
    yield race([
      call(pollExportTask, { experienceId, exportId, exportType }),
      take(
        (action) =>
          action.type === ExportTaskActions.stopPollingExportTask.getType() && action.payload.exportType === exportType
      ),
    ]);
  }
}

function* pollExportTask({ experienceId, exportId, exportType }) {
  try {
    while (true) {
      yield put(ExportTaskActions.fetchLastExport(experienceId, exportId, exportType));
      const {
        payload: { exportTask },
      } = yield take(
        (action) =>
          action.type === ExportTaskActions.fetchLastExportSuccess.getType() && action.payload.exportType === exportType
      );
      switch (exportTask.status.id) {
        case RefData.Enum.STATUS.EXPORT.FINISHED:
        case RefData.Enum.STATUS.EXPORT.FAILED:
          yield put(ExportTaskActions.completePollingExportTask(exportType));
          yield put(ExportTaskActions.stopPollingExportTask(exportType));
          break;
        default:
          // const _delay = yield call(calculatePollFrequency, { exportSize, processTime });
          yield delay(exportTask.pollingInterval);
      }
    }
  } catch (error) {
    yield put(ExportTaskActions.completePollingExportTask(exportType));
    yield put(ExportTaskActions.stopPollingExportTask(exportType));
  }
}

// dummy function in place in case we need to get more complex with the interval calculations later
// function* calculatePollFrequency({ exportSize, processTime }) {
//   return 20000;
// }

function* generateNewExportTaskHandler({ payload: { exportType } }) {
  try {
    const { id: experienceId, interactionStrategyIsPartition } = yield select(Experience.Selectors.experience);

    const _exportTask = yield call(ExportTaskApi.addExportTask, {
      id:                       experienceId,
      passwordProtectedEnabled: true,
      zipOptionsEnabled:        true,
      interactionStrategyIsPartition,
      exportType,
    });

    yield put(ExportTaskActions.generateNewExportTaskSuccess(_exportTask, exportType));

    // now we need to start polling the export task
    yield put(ExportTaskActions.startPollingExportTask(experienceId, _exportTask.exportId, exportType));
  } catch (error) {
    yield put(ExportTaskActions.generateNewExportTaskFailure(error, exportType));
  }
}

function* downloadExportHandler({ payload: { exportType } }) {
  try {
    const _exportTask = yield select(ExportTaskSelectors.activeTask, exportType);
    yield call(window.open, _exportTask.downloadFileUrl);
  } catch (error) {
    yield put(ExportTaskActions.downloadExportFailure(error, exportType));
  }
}

export default function* exportTaskSagas() {
  yield all([
    takeEvery(ExportTaskActions.generateNewExportTask.getType(), generateNewExportTaskHandler),
    takeEvery(ExportTaskActions.fetchLastExport.getType(), fetchLastExportHandler),
    takeEvery(ExportTaskActions.watchExportTaskPolling.getType(), watchExportTaskPollingHandler),
    takeEvery(ExportTaskActions.downloadExport.getType(), downloadExportHandler),
    takeEvery(ExportTaskActions.bootstrapExportTasks.getType(), bootstrapExportTasksHandler),
  ]);
}
